import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { BlogCover } from "./BlogCover";
import { ReadingTime } from "./ReadingTime";

export type CardPost = {
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  publishedAt: string;
  coverImage?: string;
  featured?: boolean;
  minutes: number;
};

export function BlogCard({
  post,
  href,
  locale,
  minLabel,
  readLabel,
}: {
  post: CardPost;
  href: string;
  locale: "it" | "en";
  minLabel: string;
  readLabel: string;
}) {
  const dateLocale = locale === "en" ? "en-GB" : "it-IT";

  return (
    <Link href={href} className="group block h-full">
      <article
        className={`flex h-full flex-col overflow-hidden rounded-2xl border bg-[hsl(var(--surface))] transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 ${
          post.featured ? "border-primary/30" : "border-[hsl(var(--border))]"
        }`}
      >
        <BlogCover title={post.title} category={post.category} coverImage={post.coverImage} className="aspect-[16/9]" />
        <div className="flex flex-1 flex-col p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="font-mono uppercase tracking-wider text-primary">{post.category}</span>
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString(dateLocale, { year: "numeric", month: "short", day: "numeric" })}
            </time>
          </div>
          <h2 className="mb-2 font-display text-xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
            {post.title}
          </h2>
          <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{post.description}</p>
          {post.tags.length > 0 && (
            <div className="mb-5 flex flex-wrap gap-1.5">
              {post.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2.5 py-0.5 text-[11px] text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-auto flex items-center justify-between gap-3 pt-1">
            {/* CTA look = bottone secondario del sito (heroOutline). La card è già il link. */}
            <span className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-primary/45 bg-transparent px-4 text-sm font-medium text-foreground transition-all duration-200 group-hover:border-primary group-hover:bg-primary/[0.08] group-hover:text-primary group-hover:shadow-[0_0_24px_hsl(var(--primary)/0.18)]">
              {readLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            <ReadingTime minutes={post.minutes} label={minLabel} className="text-xs text-muted-foreground" />
          </div>
        </div>
      </article>
    </Link>
  );
}
